/**
 * ELITE PERFORMANCE MODULE
 * Advanced performance optimizations and CLS prevention for desktop and all devices
 * Focuses on Core Web Vitals, smooth animations, and Tesla-like user experience
 */

(function() {
  'use strict';

  class ElitePerformance {
    constructor() {
      this.isInitialized = false;
      this.observers = new Map();
      this.rafCallbacks = new Set();
      this.resizeCallbacks = new Set();
      
      this.init();
    }

    init() {
      if (this.isInitialized) return;
      
      // Core optimizations
      this.setupCLSPrevention();
      this.setupImageOptimization();
      this.setupScrollOptimization();
      this.setupFontOptimization();
      this.setupCarouselOptimization();
      this.setupFormOptimization();
      this.setupLayoutStabilization();
      
      // Advanced features
      this.setupIntersectionObservers();
      this.setupPreloading();
      this.setupPerformanceMonitoring();
      
      this.isInitialized = true;
    }

    // CRITICAL: Cumulative Layout Shift Prevention
    setupCLSPrevention() {
      // Prevent layout shifts from dynamic content
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                this.stabilizeElement(node);
              }
            });
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.observers.set('cls-prevention', observer);
    }

    stabilizeElement(element) {
      // Add dimensions to images without them
      const images = element.querySelectorAll ? element.querySelectorAll('img:not([width]):not([height])') : [];
      images.forEach(img => {
        if (!img.width && !img.height) {
          // Set reasonable defaults to prevent CLS
          img.style.aspectRatio = '16/9';
          img.style.width = '100%';
          img.style.height = 'auto';
        }
      });

      // Stabilize video elements
      const videos = element.querySelectorAll ? element.querySelectorAll('video:not([width]):not([height])') : [];
      videos.forEach(video => {
        if (!video.width && !video.height) {
          video.style.aspectRatio = '16/9';
          video.style.width = '100%';
          video.style.height = 'auto';
        }
      });
    }

    // Advanced Image Optimization with CLS prevention
    setupImageOptimization() {
      // Enhanced lazy loading with size reservation
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImageWithCLSPrevention(entry.target);
            imageObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe all lazy images
      document.querySelectorAll('img[data-src], img[loading="lazy"]').forEach(img => {
        this.prepareImageForLoading(img);
        imageObserver.observe(img);
      });

      this.observers.set('image-loading', imageObserver);
    }

    prepareImageForLoading(img) {
      // Ensure dimensions are set to prevent CLS
      if (!img.width && !img.height && !img.style.aspectRatio) {
        const width = img.dataset.width || img.getAttribute('width') || 800;
        const height = img.dataset.height || img.getAttribute('height') || 600;
        
        img.style.aspectRatio = `${width}/${height}`;
        img.style.width = '100%';
        img.style.height = 'auto';
      }

      // Add loading placeholder
      if (!img.style.backgroundColor) {
        img.style.backgroundColor = '#f5f5f5';
        img.style.transition = 'background-color 0.3s ease';
      }
    }

    loadImageWithCLSPrevention(img) {
      const dataSrc = img.dataset.src;
      if (dataSrc) {
        // Preload image to prevent visual jumps
        const preloadImg = new Image();
        preloadImg.onload = () => {
          img.src = dataSrc;
          img.style.backgroundColor = 'transparent';
          img.removeAttribute('data-src');
          img.classList.add('loaded');
        };
        preloadImg.src = dataSrc;
      }
    }

    // Optimized scrolling with RAF
    setupScrollOptimization() {
      let isScrolling = false;
      let scrollTimeout;

      const handleScroll = () => {
        if (!isScrolling) {
          requestAnimationFrame(() => {
            // Batch scroll-related operations
            this.rafCallbacks.forEach(callback => callback());
            isScrolling = false;
          });
          isScrolling = true;
        }

        // Debounced scroll end
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          document.body.classList.remove('is-scrolling');
          this.onScrollEnd();
        }, 150);

        document.body.classList.add('is-scrolling');
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    onScrollEnd() {
      // Cleanup operations when scroll ends
      this.checkViewportImages();
      this.updateCarouselPositions();
    }

    // Font loading optimization to prevent FOIT/FOUT
    setupFontOptimization() {
      if ('fonts' in document) {
        // Preload critical fonts
        const criticalFonts = [
          'Inter',
          'SF Pro Display',
          'system-ui'
        ];

        criticalFonts.forEach(fontFamily => {
          document.fonts.load(`400 16px ${fontFamily}`).catch(() => {
            // Fallback handled by CSS
          });
          document.fonts.load(`500 16px ${fontFamily}`).catch(() => {
            // Fallback handled by CSS
          });
        });

        // Font loading event
        document.fonts.ready.then(() => {
          document.body.classList.add('fonts-loaded');
        });
      }
    }

    // Carousel and slider optimization
    setupCarouselOptimization() {
      const carousels = document.querySelectorAll('.hero-carousel, .tesla-carousel, .js-carousel');
      
      carousels.forEach(carousel => {
        this.optimizeCarousel(carousel);
      });
    }

    optimizeCarousel(carousel) {
      // Preload adjacent slides for smooth transitions
      const slides = carousel.querySelectorAll('.hero-slide, .tesla-carousel__card, .carousel-slide');
      
      slides.forEach((slide, index) => {
        const images = slide.querySelectorAll('img[data-src]');
        
        // Preload first 3 slides immediately
        if (index < 3) {
          images.forEach(img => this.loadImageWithCLSPrevention(img));
        }
      });

      // Optimize carousel scrolling
      const track = carousel.querySelector('.hero-carousel__slides, .tesla-carousel__track, .carousel-track');
      if (track) {
        track.style.willChange = 'transform';
        track.style.transform = 'translateZ(0)'; // GPU acceleration
      }
    }

    // Form optimization disabled to avoid injecting error placeholders
    setupFormOptimization() {}

    // Layout stabilization for dynamic content
    setupLayoutStabilization() {
      // Monitor for price updates that might cause shifts
      const priceElements = document.querySelectorAll('[data-product-price], .price, #total-price');
      
      priceElements.forEach(element => {
        // Set minimum width to prevent horizontal shifts
        if (!element.style.minWidth) {
          const currentWidth = element.offsetWidth;
          if (currentWidth > 0) {
            element.style.minWidth = currentWidth + 'px';
          }
        }
      });
    }

    // Advanced intersection observers for performance
    setupIntersectionObservers() {
      // Lazy load sections
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-viewport');
            this.activateSection(entry.target);
            sectionObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -10% 0px'
      });

      document.querySelectorAll('section[data-lazy], .lazy-section').forEach(section => {
        sectionObserver.observe(section);
      });

      this.observers.set('section-loading', sectionObserver);
    }

    activateSection(section) {
      // Load content for this section
      const lazyImages = section.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => this.loadImageWithCLSPrevention(img));

      // Start videos if autoplay is enabled
      const videos = section.querySelectorAll('video[data-autoplay="true"]');
      videos.forEach(video => {
        video.autoplay = true;
        video.play().catch(() => {}); // Handle autoplay policy
      });
    }

    // Intelligent preloading
    setupPreloading() {
      // Preload critical resources on user interaction
      const interactionEvents = ['mouseenter', 'touchstart', 'focus'];
      
      interactionEvents.forEach(event => {
        document.addEventListener(event, () => {
          this.preloadCriticalResources();
        }, { once: true, passive: true });
      });
    }

    preloadCriticalResources() {
      const resourcesToPreload = [
        '/cart',
        '/search',
        '/collections'
      ];

      resourcesToPreload.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      });
    }

    // Performance monitoring and optimization
    setupPerformanceMonitoring() {
      // Monitor CLS in real-time
      if ('PerformanceObserver' in window) {
        try {
          const clsObserver = new PerformanceObserver((list) => {
            let clsValue = 0;
            list.getEntries().forEach((entry) => {
              if (!entry.hadRecentInput) {
                clsValue += entry.value;
              }
            });
            
            if (clsValue > 0.1) {
              this.optimizeForCLS();
            }
          });
          
          clsObserver.observe({ entryTypes: ['layout-shift'] });
          this.observers.set('cls-monitor', clsObserver);
        } catch (e) {
          // PerformanceObserver not fully supported
        }
      }
    }

    optimizeForCLS() {
      // Emergency CLS fixes
      document.querySelectorAll('img:not([width]):not([height])').forEach(img => {
        if (!img.style.aspectRatio) {
          img.style.aspectRatio = '16/9';
          img.style.width = '100%';
          img.style.height = 'auto';
        }
      });
    }

    // Utility methods
    checkViewportImages() {
      const viewportImages = document.querySelectorAll('img[data-src]');
      viewportImages.forEach(img => {
        const rect = img.getBoundingClientRect();
        if (rect.top < window.innerHeight + 100) {
          this.loadImageWithCLSPrevention(img);
        }
      });
    }

    updateCarouselPositions() {
      // Update carousel indicators based on scroll position
      document.querySelectorAll('.tesla-carousel__track').forEach(track => {
        const scrollLeft = track.scrollLeft;
        const cardWidth = track.querySelector('.tesla-carousel__card')?.offsetWidth || 300;
        const currentIndex = Math.round(scrollLeft / cardWidth);
        
        // Update active dots
        const dots = track.parentElement.querySelectorAll('.tesla-carousel__dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('tesla-carousel__dot--active', index === currentIndex);
        });
      });
    }

    // Public API for external use
    addRAFCallback(callback) {
      this.rafCallbacks.add(callback);
    }

    removeRAFCallback(callback) {
      this.rafCallbacks.delete(callback);
    }

    addResizeCallback(callback) {
      this.resizeCallbacks.add(callback);
      
      // Setup resize handler if not already done
      if (this.resizeCallbacks.size === 1) {
        let resizeTimeout;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.resizeCallbacks.forEach(cb => cb());
          }, 100);
        }, { passive: true });
      }
    }

    // Cleanup method
    destroy() {
      this.observers.forEach(observer => {
        if (observer.disconnect) observer.disconnect();
      });
      this.observers.clear();
      this.rafCallbacks.clear();
      this.resizeCallbacks.clear();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.elitePerformance = new ElitePerformance();
    });
  } else {
    window.elitePerformance = new ElitePerformance();
  }

  // Make class available globally for extensions
  window.ElitePerformance = ElitePerformance;

})();
